import { appendAppLog } from '../platform/appLog';
import { schreibeBase64 } from '../platform/dateien';
import { stelleOrdnerSicher } from '../platform/ordner';

/**
 * Wohin die **Anhänge einer Prüfung** gehören und wie sie dort hinkommen — für beide Gleise.
 *
 * ⚠️ **Das ist die Ablage, nicht das Protokoll.** Classic und Relay holen ihre Dateien über
 * verschiedene Adressen und mit verschiedenen Kennungen; wo sie danach liegen und wie sie
 * heissen, ist dieselbe Frage. Genau dafür lässt [[zweigleisig]] Gemeinsames zu: „Gemeinsam
 * bleibt nur, was wirklich beiden dient (Ablage, Dateinamen, Beglaubigung)."
 *
 * ⚠️ **Beim Download, alle auf einmal** (Entscheidung des Nutzers, 2026-09-13): Danach ist die
 * Prüfung vollständig offline. Während einer Prüfung steht das iPad womöglich ohne Netz — was
 * dann nicht auf der Platte liegt, ist für den Prüfer nicht vorhanden.
 *
 * ⚠️ **Ein Fehlschlag bricht den Download nicht ab.** Eine Prüfung ohne ein Anhangsbild ist
 * brauchbar; eine gar nicht geladene ist es nicht.
 */

/** Wo die Anhänge liegen. **Nicht** unter `logs/` — dort liegen die Aufnahmen des Prüfers. */
export const MEDIEN_WURZEL = 'media';

/**
 * Was im Dateinamen stehen darf.
 *
 * Classic-Kennungen tragen Doppelpunkte (`classic:media:12`). Auf dem Dateisystem haben die
 * nichts verloren — je nach Weg werden sie umgedeutet oder verschluckt.
 */
const dateiTauglich = (text: string) => text.replace(/[^A-Za-z0-9._-]+/g, '-');

/**
 * Die Endung aus dem Medientyp.
 *
 * Unbekanntes wird **nicht** endungslos abgelegt: eine Datei ohne Endung lässt sich später von
 * keiner Anzeige mehr deuten. `image/webp` wird deshalb zu `.webp`, und erst wenn gar nichts
 * Brauchbares dasteht, zu `.bin`.
 */
export function endungVonMime(mime: string): string {
  const bekannt: Record<string, string> = {
    'image/jpeg': 'jpeg',
    'image/jpg': 'jpeg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'application/pdf': 'pdf',
  };
  const sauber = mime.trim().toLowerCase();
  if (bekannt[sauber]) return bekannt[sauber];
  const teil = sauber.split('/')[1] ?? '';
  const roh = teil.split(';')[0].replace(/[^a-z0-9]/g, '');
  return roh === '' ? 'bin' : roh;
}

/** `media/<Prüfung>/<Medium>.<Endung>`, alles dateitauglich gemacht. */
export function medienPfad(examId: string, mediaId: string, mime: string): string {
  return (
    MEDIEN_WURZEL +
    '/' +
    dateiTauglich(examId) +
    '/' +
    dateiTauglich(mediaId) +
    '.' +
    endungVonMime(mime)
  );
}

/** Ein Anhang, wie ihn die Abbildung des jeweiligen Gleises herausgibt. */
export interface MediumZumHolen {
  /** Die **rohe** Kennung des Servers — sie steht so in seiner Adresse. */
  id: string | number;
  mime: string;
}

export interface MedienDeps {
  examId: string;
  /** Holt eine Rohdatei als Base64. Einschiebbar für Tests. */
  hole: (id: string) => Promise<string>;
  /**
   * Wie aus der rohen Serverkennung der Name in der Ablage wird.
   *
   * Classic braucht das: dort steht in der Tabelle `classic:media:12`, in der Adresse aber die
   * nackte `12`. Relay lässt es weg — seine Kennungen sind schon unsere.
   */
  ablageId?: (id: string | number) => string;
  schreibe?: (pfad: string, base64: string) => Promise<void>;
  log?: (zeile: string) => Promise<void>;
}

export interface MedienErgebnis {
  geholt: number;
  gescheitert: number;
}

/** Legt eine Rohdatei unter Documents ab. */
async function schreibeDatei(pfad: string, base64: string): Promise<void> {
  const ordner = pfad.slice(0, pfad.lastIndexOf('/'));
  await stelleOrdnerSicher(ordner);
  await schreibeBase64(pfad, base64);
}

export async function holeMedien(
  medien: MediumZumHolen[],
  deps: MedienDeps,
): Promise<MedienErgebnis> {
  const schreibe = deps.schreibe ?? schreibeDatei;
  const log = deps.log ?? appendAppLog;
  const ablageId = deps.ablageId ?? ((id: string | number) => String(id));
  let geholt = 0;
  let gescheitert = 0;

  for (const medium of medien) {
    try {
      const base64 = await deps.hole(String(medium.id));
      await schreibe(medienPfad(deps.examId, ablageId(medium.id), medium.mime), base64);
      geholt += 1;
    } catch (fehler) {
      gescheitert += 1;
      await log('Anhang ' + medium.id + ' liess sich nicht holen: ' + String(fehler));
    }
  }

  return { geholt, gescheitert };
}
